/**
 * GA4 Custom Event Tracking for SchoolMapper
 *
 * Uses the existing GA4 installation (window.gtag) already configured in layout.tsx.
 * All functions are safe to call in any environment — they no-op if gtag is not available.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function fireEvent(eventName: string, params: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
}

// ─── 1. school_search ────────────────────────────────────────────────────────
export interface SchoolSearchParams {
  search_location?: string;
  community?: string;
  curriculum?: string;
  khda_rating?: string;
  fee_filter?: string;
  results_count?: number;
}

export function trackSchoolSearch(params: SchoolSearchParams) {
  fireEvent('school_search', params);
}

// ─── 2. community_search ─────────────────────────────────────────────────────
export interface CommunitySearchParams {
  school_name?: string;
  community?: string;
  results_count?: number;
}

export function trackCommunitySearch(params: CommunitySearchParams) {
  fireEvent('community_search', params);
}

// ─── 3. school_profile_view ──────────────────────────────────────────────────
export interface SchoolProfileViewParams {
  school_name: string;
  school_slug: string;
  curriculum: string;
  khda_rating: string;
}

export function trackSchoolProfileView(params: SchoolProfileViewParams) {
  fireEvent('school_profile_view', params);
}

// ─── 4. commute_calculated ───────────────────────────────────────────────────
export interface CommuteCalculatedParams {
  school_name: string;
  community_name: string;
  commute_minutes: number;
  direction?: 'community_to_school' | 'school_to_community';
}

export function trackCommuteCalculated(params: CommuteCalculatedParams) {
  fireEvent('commute_calculated', params);
}

// ─── 5. add_to_shortlist ─────────────────────────────────────────────────────
export interface ShortlistParams {
  school_name: string;
  school_slug: string;
}

export function trackAddToShortlist(params: ShortlistParams) {
  fireEvent('add_to_shortlist', params);
}

// ─── 6. remove_from_shortlist ────────────────────────────────────────────────
export function trackRemoveFromShortlist(params: ShortlistParams) {
  fireEvent('remove_from_shortlist', params);
}

// ─── 7. compare_schools ──────────────────────────────────────────────────────
export interface CompareSchoolsParams {
  number_of_schools: number;
  school_names?: string;
}

export function trackCompareSchools(params: CompareSchoolsParams) {
  fireEvent('compare_schools', params);
}

// ─── 8. admissions_click ─────────────────────────────────────────────────────
export interface AdmissionsClickParams {
  school_name: string;
  destination_url: string;
}

export function trackAdmissionsClick(params: AdmissionsClickParams) {
  fireEvent('admissions_click', params);
}

// ─── 9. school_website_click ─────────────────────────────────────────────────
export interface SchoolWebsiteClickParams {
  school_name: string;
  destination_url: string;
}

export function trackSchoolWebsiteClick(params: SchoolWebsiteClickParams) {
  fireEvent('school_website_click', params);
}

// ─── 10. phone_click ─────────────────────────────────────────────────────────
export interface PhoneClickParams {
  school_name: string;
}

export function trackPhoneClick(params: PhoneClickParams) {
  fireEvent('phone_click', params);
}

// ─── 11. email_click ─────────────────────────────────────────────────────────
export interface EmailClickParams {
  school_name: string;
}

export function trackEmailClick(params: EmailClickParams) {
  fireEvent('email_click', params);
}

// ─── 12. community_profile_view ──────────────────────────────────────────────
export interface CommunityProfileViewParams {
  community_name: string;
  community_slug: string;
}

export function trackCommunityProfileView(params: CommunityProfileViewParams) {
  fireEvent('community_profile_view', params);
}
